/**
 * Version bookkeeping for the board's localStorage cache — the re-arming half of the
 * payload-version mechanism.
 *
 * The board deliberately never refetches on its own; instead it asks `GET /payload-version` on
 * mount and discards a cached payload from a different build / prompt / interview-links revision.
 * That check used to run exactly ONCE per recruiter selection, which left two holes
 * (`/ta-action-boards/:tab` is one mounted view, so switching tabs re-runs nothing):
 *
 * 1. A link created while the board was already open never surfaced — not on tab switches, not on
 *    window focus — because nothing ever re-asked. This hook re-asks on window focus, on
 *    visibility, and via `recheck()` on sub-tab changes, throttled to one check per minute. An
 *    EXPLICIT mismatch surfaces as `staleVersion`, and the view re-runs the same sanctioned
 *    mount-time path (read cache under the new version → discarded → fresh pull); this is NOT a
 *    new auto-refetch, it is the existing invalidation re-armed.
 * 2. The write-back effect stamped every persisted board with the version fetched at MOUNT. An
 *    in-memory board that had meanwhile gone stale (e.g. another tab cleared the cache after a
 *    link edit) was thereby re-persisted as if it satisfied the newest version. The stamp now
 *    lives in `boardVersion()`/`markBoardVersion()`: a board is stamped with the version it is
 *    actually KNOWN to satisfy — the cached stamp it was read under, or the latest known server
 *    version at the moment a fresh Lever pull landed — never with a newer one it merely outlived.
 *
 * Kept out of TAActionBoardView.tsx for the 500-line rule.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../../services/taActionBoardApi';
import { versionsMatch } from './boardCache';

/** Re-asking more often than this buys nothing — the endpoint is cheap, but focus events are noisy. */
export const RECHECK_THROTTLE_MS = 60_000;

export function useBoardVersion() {
  /** The newest version string the server has reported this session. */
  const latestVersionRef = useRef<string | null>(null);
  /** The version the CURRENT in-memory board is known to satisfy — what write-back may stamp. */
  const boardVersionRef = useRef<string | null>(null);
  const lastCheckedAtRef = useRef(0);
  /**
   * Set on an EXPLICIT mismatch between the in-memory board and the server. The view consumes it
   * (when no modal/countdown is mid-flight) by re-running the mount-time cache path; rendering any
   * payload clears it again via the mark* calls below.
   */
  const [staleVersion, setStaleVersion] = useState<string | null>(null);

  /** Unthrottled check for the mount path. Returns null when the check itself could not run. */
  const checkNow = useCallback(async (): Promise<string | null> => {
    lastCheckedAtRef.current = Date.now();
    const version = await api.fetchPayloadVersion();
    if (version) latestVersionRef.current = version;
    return version;
  }, []);

  /** The board now renders a payload known to satisfy `version` (a cached stamp, possibly absent). */
  const markBoardVersion = useCallback((version: string | null | undefined) => {
    boardVersionRef.current = version ?? null;
    // Whatever was stale has just been replaced; staleness is re-judged at the next check.
    setStaleVersion(null);
  }, []);

  /**
   * The board now renders a payload fresh from the server, stamped with the latest version this
   * client has SEEN — not one the payload itself proved. Known bounded edge: with several Cloud
   * Run instances, a fresh pull can be served from another instance's ≤60s server-side board
   * cache that predates a link write, and this stamp then keeps it "current" until a manual
   * Refresh. Closing it for real means the board response carrying its own build-time version;
   * accepted for now because link writes clear the writing instance's cache and multiple
   * concurrent instances are rare at this traffic.
   */
  const markBoardFresh = useCallback(() => {
    boardVersionRef.current = latestVersionRef.current;
    setStaleVersion(null);
  }, []);

  /** What the persistence effect should stamp the cached payload with. */
  const boardVersion = useCallback(() => boardVersionRef.current, []);

  /**
   * Consume the staleness flag BEFORE acting on it. The consumer must clear first: clearing only
   * via a successful render leaves the flag set when the triggered pull FAILS, and the view's
   * effect then re-fires on every loading flip — an unthrottled retry storm of full Lever pulls.
   */
  const clearStale = useCallback(() => setStaleVersion(null), []);

  /**
   * Throttled re-check. Flags staleness only on an EXPLICIT mismatch — a failed check returns
   * null and concludes nothing, and `versionsMatch` already treats a server-side `unknown`
   * links revision as inconclusive. A full board rebuild is hundreds of Lever calls, so nothing
   * here may trigger one on a hunch.
   */
  const recheck = useCallback(() => {
    if (Date.now() - lastCheckedAtRef.current < RECHECK_THROTTLE_MS) return;
    lastCheckedAtRef.current = Date.now();
    void api.fetchPayloadVersion().then((version) => {
      if (!version) return;
      latestVersionRef.current = version;
      if (versionsMatch(boardVersionRef.current, version)) return;
      setStaleVersion(version);
    });
  }, []);

  useEffect(() => {
    const onFocus = () => recheck();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') recheck();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [recheck]);

  return { checkNow, recheck, markBoardVersion, markBoardFresh, boardVersion, staleVersion, clearStale };
}
